/**
 * repositories/userSyncSettingsRepository.ts
 * User Sync Settings table data access layer
 *
 * Architecture: WU1.5 Task 10 - UserSyncSettings Repository Pattern
 *
 * IMPORTANT PATTERNS:
 * 1. One row per user - enforced via unique constraint on user_id
 *    - getOrCreateByUserId uses upsert for race-proof creation
 *    - Multiple tabs/devices can safely call getOrCreate simultaneously
 *
 * 2. Optimistic concurrency with version field
 *    - update() accepts optional expectedVersion parameter
 *    - When provided: WHERE user_id = ? AND version = expectedVersion
 *    - When omitted: Unconditional update (admin override / system repair path)
 *    - Version auto-increments on each update (DB trigger handles this)
 *
 * 3. Default values from domain types
 *    - New rows use DEFAULT_USER_SYNC_SETTINGS constant
 *    - Ensures consistency between DB defaults and application expectations
 *
 * 4. Identity field immutability
 *    - user_id and id cannot be updated (treat as immutable)
 *    - These are identity fields for the record
 */

import { supabase } from '@/lib/supabase';
import { toDomain, toWire, toPatch } from '@/lib/supabaseMappers';
import type { Database } from '@/types/supabase.types';
import type {
  UserSyncSettings,
  UserSyncSettingsInsert,
  UserSyncSettingsUpdate
} from '@/types/supabase/UserSyncSettings';
import { DEFAULT_USER_SYNC_SETTINGS } from '@/types/supabase/UserSyncSettings';
import type { UserSyncSettingsWithStatus } from '@/types/supabase/UserSyncSettingsWithStatus';

// ============================================================================
// WIRE TYPES (snake_case from database)
// ============================================================================

type UserSyncSettingsRow = Database['public']['Tables']['user_sync_settings']['Row'];
type UserSyncSettingsInsertRow = Database['public']['Tables']['user_sync_settings']['Insert'];
type UserSyncSettingsUpdateRow = Database['public']['Tables']['user_sync_settings']['Update'];

// ============================================================================
// PROJECTION CONSTANTS
// ============================================================================

/**
 * Standard user sync settings projection
 * NOTE: If you add a column to `user_sync_settings`, update this list AND the UserSyncSettings type.
 */
const SETTINGS_PROJECTION = 'id,user_id,active_sync_provider,sync_enabled,sync_interval,sync_on_login,sync_on_session_close,conflict_resolution,version,created_at,updated_at' as const;

// ============================================================================
// ERROR CLASSES
// ============================================================================

/**
 * Conflict error thrown when optimistic concurrency check fails
 */
export class ConflictError extends Error {
  constructor(message: string, public data?: { current?: UserSyncSettings }) {
    super(message);
    this.name = 'ConflictError';
  }
}

// ============================================================================
// REPOSITORY
// ============================================================================

/**
 * User Sync Settings Repository
 *
 * Provides type-safe data access for user_sync_settings table with centralized mapping
 * between database snake_case and application camelCase.
 *
 * Contract: One row per user (enforced by unique constraint on user_id)
 *
 * Key Features:
 * - Race-proof creation via upsert pattern
 * - Optimistic concurrency control with version field
 * - Column projection for performance
 * - Comprehensive error handling
 */
export const userSyncSettingsRepository = {
  /**
   * Get user's sync settings or create defaults if none exist
   *
   * PATTERN: Race-proof upsert - safe to call from multiple tabs/devices simultaneously
   *
   * Implementation Details:
   * - Uses upsert with onConflict: 'user_id', ignoreDuplicates: true
   * - If row exists: Returns existing settings (no modification)
   * - If row doesn't exist: Creates with DEFAULT_USER_SYNC_SETTINGS + userId
   * - New rows start at version 1 (DB default)
   * - Always returns camelCase UserSyncSettings (never wire shape)
   *
   * Use Cases:
   * - First-time user initialization
   * - Multi-tab safety (tabs can race to create, one wins, all get same result)
   * - Service layer calls this to ensure settings exist before operating
   *
   * @param userId - User ID (foreign key to auth.users)
   * @returns Promise<UserSyncSettings> - User's sync settings (existing or newly created)
   * @throws Error if database operation fails
   */
  async getOrCreateByUserId(userId: string): Promise<UserSyncSettings> {
    // Try to fetch existing settings first
    const { data: existing, error: fetchError } = await supabase
      .from('user_sync_settings')
      .select(SETTINGS_PROJECTION)
      .eq('user_id', userId)
      .maybeSingle<UserSyncSettingsRow>();

    if (fetchError) throw fetchError;

    // If settings exist, return them
    if (existing) {
      return toDomain<UserSyncSettings>(existing);
    }

    // Settings don't exist, create with defaults
    const defaultSettings: UserSyncSettingsInsert = {
      userId,
      ...DEFAULT_USER_SYNC_SETTINGS
    };

    const wireData = toWire<UserSyncSettingsInsertRow>(defaultSettings);

    const { data, error } = await supabase
      .from('user_sync_settings')
      .insert(wireData)
      .select(SETTINGS_PROJECTION)
      .single<UserSyncSettingsRow>();

    if (error) {
      // Handle race condition: another tab/device created the row
      if (error.code === '23505') { // unique_violation
        // Retry fetch
        const { data: retryData, error: retryError } = await supabase
          .from('user_sync_settings')
          .select(SETTINGS_PROJECTION)
          .eq('user_id', userId)
          .single<UserSyncSettingsRow>();

        if (retryError) throw retryError;
        if (!retryData) throw new Error('Failed to get user sync settings after race condition');

        return toDomain<UserSyncSettings>(retryData);
      }

      throw error;
    }

    if (!data) throw new Error('Failed to create user sync settings');

    return toDomain<UserSyncSettings>(data);
  },

  /**
   * Get user's sync settings without creation
   *
   * PATTERN: Simple fetch with nullable return
   *
   * Use Cases:
   * - Check if settings exist before upsert
   * - Conditional logic based on settings existence
   * - Admin tools viewing user configuration
   *
   * @param userId - User ID
   * @returns Promise<UserSyncSettings | null> - Settings if found, null otherwise
   * @throws Error if database operation fails
   */
  async getByUserId(userId: string): Promise<UserSyncSettings | null> {
    const { data, error } = await supabase
      .from('user_sync_settings')
      .select(SETTINGS_PROJECTION)
      .eq('user_id', userId)
      .maybeSingle<UserSyncSettingsRow>();

    if (error) throw error;
    if (!data) return null;

    return toDomain<UserSyncSettings>(data);
  },

  /**
   * Update user sync settings with validation and optimistic concurrency
   *
   * REFACTORED FOR TASK 2.7: Now uses validation RPC instead of direct UPDATE
   *
   * Single Write Path Pattern:
   * - Calls update_user_sync_settings() RPC function
   * - RPC owns BOTH version checking AND business validation
   * - Validation: Prevents enabling sync without active OAuth connection
   * - Optimistic concurrency: Enforces version = expectedVersion when provided
   *
   * Error Handling:
   * - P0002: Version conflict (optimistic concurrency failure) → ConflictError
   * - P0001: Business rule violation (e.g., no connection) → ValidationError
   * - Other: Propagates original error
   *
   * Version Behavior:
   * - When expectedVersion IS provided: RPC enforces version check
   * - When expectedVersion IS NOT provided: Unconditional update
   * - Database trigger auto-increments version on each update
   *
   * Partial Update Hygiene:
   * - Uses toPatch() to strip undefined keys
   * - Passing null will set column to NULL in database
   * - Identity fields (user_id, id) should NOT be in updates object
   *
   * @param userId - User ID (not used, auth.uid() enforced in RPC)
   * @param updates - Partial settings to update (camelCase)
   * @param expectedVersion - Optional version for optimistic concurrency
   * @returns Promise<UserSyncSettings> - Updated settings
   * @throws ConflictError if version mismatch (P0002)
   * @throws Error if validation fails (P0001) or other database error
   */
  async update(
    _userId: string, // Unused: auth.uid() enforced in RPC
    updates: UserSyncSettingsUpdate,
    expectedVersion?: number
  ): Promise<UserSyncSettings> {
    // Convert updates to wire format (strips undefined)
    const wireUpdates = toPatch<UserSyncSettingsUpdateRow>(updates);

    // Call validation RPC instead of direct UPDATE (single write path)
    const { data, error } = await supabase.rpc('update_user_sync_settings',
      expectedVersion !== undefined
        ? { p_updates: wireUpdates, p_expected_version: expectedVersion }
        : { p_updates: wireUpdates }
    );

    if (error) {
      // Map database errors to domain errors
      // P0002 = Version conflict (optimistic concurrency failure)
      if (error.code === 'P0002') {
        throw new ConflictError(
          'Settings were modified by another device. Please refresh and try again.'
        );
      }

      // P0001 = Business rule violation (e.g. enabling sync without connection)
      if (error.code === 'P0001') {
        throw new Error('Cannot enable sync: provider not connected');
      }

      // Unknown error
      throw error;
    }

    if (!data || (Array.isArray(data) && data.length === 0)) {
      throw new Error('Update succeeded but no data returned');
    }

    const rowData = Array.isArray(data) ? data[0] : data;
    return toDomain<UserSyncSettings>(rowData);
  },

  /**
   * Get user's sync settings with live connection status
   *
   * NEW FOR TASK 2.7: Query derived view for single-call settings + connection status
   *
   * Pattern: Read from v_user_sync_settings_with_status view
   * - View combines user_sync_settings + oauth_cloud_tokens
   * - Returns settings + isConnected (boolean) + providerEmail + folderId
   * - Filtered by auth.uid() in view (RLS enforced)
   *
   * Use Cases:
   * - Component can get settings + connection status in one query
   * - Reduces need for separate checkGoogleDriveConnection() call
   * - Not yet used by components (migration deferred to Task 2.8)
   *
   * @param userId - User ID (filtered by auth.uid() in view)
   * @returns Promise<UserSyncSettingsWithStatus> - Settings with connection status
   * @throws Error if database operation fails or settings not found
   */
  async getSettingsWithStatus(userId: string): Promise<UserSyncSettingsWithStatus> {
    const { data, error } = await supabase
      .from('v_user_sync_settings_with_status')
      .select('*')
      .eq('user_id', userId)
      .single();

    if (error) throw error;
    if (!data) throw new Error('Settings with status not found');

    return toDomain<UserSyncSettingsWithStatus>(data);
  }
};
